import React, { createContext, useContext, useMemo } from 'react';

const PermissionsContext = createContext(null);

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within PermissionsProvider');
  }
  return context;
};

/**
 * Matrice de permissions frontend (synchronisée avec backend)
 * Format: { "RoleName": { "resource": ["action1", "action2"] } }
 * 
 * Note: Pour les rôles personnalisés, les permissions sont chargées dynamiquement depuis l'API
 */
const ROLE_PERMISSIONS = {
  SuperAdmin: {
    dashboard: ['read'],
    kpi: ['read'],
    finance: ['read', 'export'],
    chauffeurs: ['read', 'create', 'update', 'delete', 'export', 'import'],
    vehicules: ['read', 'create', 'update', 'delete', 'export', 'import'],
    proprietaires: ['read', 'create', 'update', 'delete', 'export', 'import'],
    intermediaires: ['read', 'create', 'update', 'delete', 'export'],
    societes: ['read', 'create', 'update', 'delete', 'export'],
    operations: ['read', 'create', 'update', 'delete', 'export', 'import'],
    incidents: ['read', 'create', 'update', 'delete', 'export'],
    alerts: ['read', 'create', 'update', 'delete'],
    users: ['read', 'create', 'update', 'delete'],
    rbac: ['read', 'view_roles', 'create_roles', 'update_roles', 'delete_roles', 'view_permissions', 'assign_roles_to_users'],
    audit_logs: ['read', 'export'],
    backups: ['read', 'create', 'delete'],
    settings: ['read', 'update'],
    import: ['import'],
  },
  
  Admin: {
    dashboard: ['read'],
    kpi: ['read'],
    finance: ['read', 'export'],
    chauffeurs: ['read', 'create', 'update', 'delete', 'export', 'import'],
    vehicules: ['read', 'create', 'update', 'delete', 'export', 'import'],
    proprietaires: ['read', 'create', 'update', 'delete', 'export', 'import'],
    intermediaires: ['read', 'create', 'update', 'delete', 'export'],
    societes: ['read', 'create', 'update', 'delete', 'export'],
    operations: ['read', 'create', 'update', 'delete', 'export', 'import'],
    incidents: ['read', 'create', 'update', 'delete', 'export'],
    alerts: ['read', 'create', 'update', 'delete'],
    users: ['read', 'create', 'update', 'delete'],
    rbac: ['read', 'view_roles', 'create_roles', 'update_roles', 'delete_roles', 'view_permissions', 'assign_roles_to_users'],
    audit_logs: ['read', 'export'],
    backups: ['read'],
    settings: ['read', 'create', 'update', 'delete'],
    import: ['import'],
  },
  
  Manager: {
    dashboard: ['read'],
    kpi: ['read'],
    finance: ['read'], // Lecture seule, pas d'export
    chauffeurs: ['read', 'create', 'update', 'delete', 'export', 'import'],
    vehicules: ['read', 'create', 'update', 'delete', 'export', 'import'],
    proprietaires: ['read', 'create', 'update', 'delete', 'export', 'import'],
    intermediaires: ['read', 'create', 'update', 'delete', 'export'],
    societes: ['read', 'create', 'update', 'delete', 'export'],
    operations: ['read', 'create', 'update', 'delete', 'export', 'import'],
    incidents: ['read', 'create', 'update', 'delete', 'export'],
    alerts: ['read', 'create', 'update', 'delete'],
    import: ['import'],
  },
  
  Operateur: {
    dashboard: ['read'],
    kpi: ['read'],
    chauffeurs: ['read', 'create', 'update'],
    vehicules: ['read', 'create', 'update'],
    proprietaires: ['read'],
    intermediaires: ['read'],
    societes: ['read'],
    operations: ['read', 'create', 'update', 'delete'],  // Ajout permission delete
    incidents: ['read', 'create', 'update'],
    alerts: ['read'],
    finance: ['read'],  // Ajout accès lecture au module finance
    // Note: users.read retiré - l'accès à la page Utilisateurs est réservé aux Admins
  },
  
  Finance: {
    dashboard: ['read'],
    kpi: ['read'],
    finance: ['read', 'export'],
    chauffeurs: ['read'],
    vehicules: ['read'],
    proprietaires: ['read'],
    intermediaires: ['read'],
    societes: ['read'],
    operations: ['read'],
    incidents: ['read'],
    alerts: ['read'],
  },
  
  Auditeur: {
    dashboard: ['read'],
    kpi: ['read'],
    finance: ['read'], // Pas d'export
    chauffeurs: ['read'],
    vehicules: ['read'],
    proprietaires: ['read'],
    intermediaires: ['read'],
    societes: ['read'],
    operations: ['read'],
    incidents: ['read'],
    alerts: ['read'],
    audit_logs: ['read'],
  },
};

/**
 * Mapping des routes vers les ressources
 */
const ROUTE_RESOURCE_MAP = {
  '/': 'dashboard',
  '/kpi-phase1': 'kpi',
  '/finance': 'finance',
  '/chauffeurs': 'chauffeurs',
  '/vehicules': 'vehicules',
  '/proprietaires': 'proprietaires',
  '/proprietaires/import': 'proprietaires',
  '/intermediaires': 'intermediaires',
  '/societes': 'societes',
  '/operations': 'operations',
  '/incidents': 'incidents',
  '/alerts': 'alerts',
  '/import': 'import',
  '/users': 'users',
  '/rbac-management': 'rbac',
  '/audit-logs': 'audit_logs',
  '/sauvegardes': 'backups',
};

export const PermissionsProvider = ({ user, children }) => {
  const [customRolePermissions, setCustomRolePermissions] = React.useState(null);
  const [isLoadingCustomRole, setIsLoadingCustomRole] = React.useState(false);
  const [roleCheckInterval, setRoleCheckInterval] = React.useState(null);
  
  // Vérifier si le rôle de l'utilisateur a changé côté serveur
  const checkRoleUpdate = React.useCallback(async () => {
    if (!user || !user.id) return;
    
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const currentUser = await response.json();
        
        // Si le rôle a changé, forcer une reconnexion
        if (currentUser.role !== user.role) {
          console.warn('[RBAC] Rôle modifié côté serveur. Déconnexion nécessaire.');
          
          // Afficher un message à l'utilisateur
          if (window.confirm(
            'Votre rôle a été modifié par un administrateur. Vous devez vous reconnecter pour que les changements prennent effet. Se reconnecter maintenant ?'
          )) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
          }
        }
      }
    } catch (error) {
      console.error('[RBAC] Erreur lors de la vérification du rôle:', error);
    }
  }, [user]);
  
  // Vérifier le rôle périodiquement (toutes les 30 secondes)
  React.useEffect(() => {
    if (!user) {
      if (roleCheckInterval) {
        clearInterval(roleCheckInterval);
        setRoleCheckInterval(null);
      }
      return;
    }
    
    // Première vérification immédiate
    checkRoleUpdate();
    
    // Puis vérifications périodiques
    const interval = setInterval(checkRoleUpdate, 30000); // 30 secondes
    setRoleCheckInterval(interval);
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [user, checkRoleUpdate]);
  
  // Charger les permissions des rôles personnalisés depuis l'API
  React.useEffect(() => {
    if (!user || !user.role) return;
    
    // Si le rôle existe dans ROLE_PERMISSIONS, c'est un rôle système
    if (ROLE_PERMISSIONS[user.role]) {
      setCustomRolePermissions(null);
      return;
    }
    
    // Sinon, c'est un rôle personnalisé, on charge ses permissions
    const fetchCustomRolePermissions = async () => {
      setIsLoadingCustomRole(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rbac/roles`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const roles = await response.json();
          const customRole = roles.find(r => r.name === user.role && r.type === 'custom');
          
          if (customRole && customRole.permissions) {
            // Transformer les permissions du format backend vers le format frontend
            const transformedPermissions = {};
            customRole.permissions.forEach(perm => {
              const [resource, action] = perm.split('.');
              if (!transformedPermissions[resource]) {
                transformedPermissions[resource] = [];
              }
              transformedPermissions[resource].push(action);
            });
            setCustomRolePermissions(transformedPermissions);
          } else {
            setCustomRolePermissions({});
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement des permissions du rôle personnalisé:', error);
        setCustomRolePermissions({});
      } finally {
        setIsLoadingCustomRole(false);
      }
    };
    
    fetchCustomRolePermissions();
  }, [user]);
  
  const permissions = useMemo(() => {
    if (!user || !user.role) return {};
    
    // Rôle système
    if (ROLE_PERMISSIONS[user.role]) {
      return ROLE_PERMISSIONS[user.role];
    }
    
    // Rôle personnalisé (utiliser les permissions chargées depuis l'API)
    return customRolePermissions || {};
  }, [user, customRolePermissions]);
  
  /**
   * Vérifier si l'utilisateur a une permission spécifique
   * @param {string} resource - Nom de la ressource (ex: "chauffeurs", "finance")
   * @param {string} action - Action (ex: "read", "create", "update", "delete", "export")
   * @returns {boolean}
   */
  const hasPermission = (resource, action) => {
    const resourcePermissions = permissions[resource] || [];
    return resourcePermissions.includes(action);
  };
  
  /**
   * Vérifier si l'utilisateur peut accéder à une route
   * @param {string} routePath - Chemin de la route (ex: "/chauffeurs", "/finance")
   * @returns {boolean}
   */
  const canAccessRoute = (routePath) => {
    const resource = ROUTE_RESOURCE_MAP[routePath];
    if (!resource) return true; // Route non mappée, accès autorisé par défaut
    return hasPermission(resource, 'read');
  };
  
  /**
   * Obtenir toutes les routes accessibles pour l'utilisateur
   * @returns {string[]} Liste des chemins de routes accessibles
   */
  const getAccessibleRoutes = () => {
    return Object.keys(ROUTE_RESOURCE_MAP).filter(route => canAccessRoute(route));
  };
  
  /**
   * Vérifier si l'utilisateur a au moins une permission parmi plusieurs
   * @param {string} resource - Nom de la ressource
   * @param {string[]} actions - Liste d'actions
   * @returns {boolean}
   */
  const hasAnyPermission = (resource, actions) => {
    return actions.some(action => hasPermission(resource, action));
  };
  
  const value = {
    permissions,
    hasPermission,
    canAccessRoute,
    getAccessibleRoutes,
    hasAnyPermission,
    userRole: user?.role,
    isLoadingCustomRole,
  };
  
  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};
